import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Microphone, Headphones, PencilSimple, Chats, Certificate, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const TABS = [["lesson", "Words", BookOpen], ["writing", "Writing", PencilSimple],
  ["speaking", "Speaking", Microphone], ["listening", "Listening", Headphones],
  ["tutor", "Tutor", Chats]];

export default function English() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("lesson");
  const [lesson, setLesson] = useState(null);
  const [picked, setPicked] = useState({});
  const [marks, setMarks] = useState(null);
  const [writing, setWriting] = useState({ task: "Some people think young adults should learn a trade rather than go to university. Discuss both views and give your own opinion.", text: "" });
  const [written, setWritten] = useState(null);
  const [spoken, setSpoken] = useState(null);
  const [recording, setRecording] = useState(false);
  const [heard, setHeard] = useState(null);
  const [hAnswers, setHAnswers] = useState({});
  const [hMarks, setHMarks] = useState(null);
  const [chat, setChat] = useState([]);
  const [say, setSay] = useState("");
  const [session, setSession] = useState("");
  const [aim, setAim] = useState({ exam: "IELTS", target_band: 7, country: "", first_language: "" });
  const [seats, setSeats] = useState("");
  const recorder = useRef(null);
  const chunks = useRef([]);
  const started = useRef(0);

  const load = useCallback((first = false) => {
    api.get("/english").then(({ data: d }) => {
      setData(d);
      if (first) {
        setAim({ exam: d.progress.exam || "IELTS", target_band: d.progress.target_band || 7,
          country: d.progress.country || "", first_language: d.progress.first_language || "" });
      }
    }).catch(oops);
  }, []);
  useEffect(() => { load(true); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(ok); load(); return r; }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const newLesson = async () => {
    setBusy(true); setMarks(null); setPicked({});
    try {
      const { data: out } = await api.post("/english/lesson", {
        exam: aim.exam, country: aim.country, first_language: aim.first_language });
      setLesson(out); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const markLesson = async () => {
    const answers = lesson.questions.map((_, i) => (picked[i] ?? -1));
    if (answers.some((a) => a < 0)) return toast.error("Answer every question first");
    const r = await act(() => api.post(`/english/lesson/${lesson.id}/answers`, { answers }),
      "Marked");
    if (r) setMarks(r.data);
  };

  const record = async () => {
    if (recording) {
      recorder.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      started.current = Date.now();
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const seconds = (Date.now() - started.current) / 1000;
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "answer.webm");
        form.append("prompt", "Describe a place in your country that you would take a visitor to, and say why.");
        form.append("seconds", String(Math.round(seconds)));
        setBusy(true);
        try {
          const { data: out } = await api.post("/english/speaking", form);
          setSpoken(out); load();
        } catch (e) { oops(e); } finally { setBusy(false); }
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("We could not reach your microphone — allow it and try again");
    }
  };

  const newListening = async () => {
    setBusy(true); setHMarks(null); setHAnswers({});
    try {
      const { data: out } = await api.post("/english/listening", { exam: aim.exam });
      setHeard(out);
      const speech = new SpeechSynthesisUtterance(out.passage);
      speech.lang = "en-GB";
      speech.rate = out.speed === "slow" ? 0.85 : 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(speech);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const sendChat = async () => {
    if (!say.trim()) return;
    const mine = say.trim();
    setChat((c) => [...c, { who: "learner", text: mine }]);
    setSay(""); setBusy(true);
    try {
      const { data: out } = await api.post("/english/tutor", { message: mine, session_id: session });
      setSession(out.session_id);
      setChat((c) => [...c, { who: "tutor", text: out.reply }]);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const subscribe = async (plan) => {
    try {
      const { data: out } = await api.post("/payments/english/subscribe", {
        plan, origin_url: window.location.origin });
      window.location.href = out.url;
    } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Opening the English desk…</p>;
  const p = data.progress;
  const a = data.access;

  return (
    <div className="space-y-6" data-testid="english-page">
      <div>
        <p className="overline text-[#B3241B]">English, by the root</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <BookOpen size={30} weight="duotone" className="text-[#B3241B]" /> Word power
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="english-explainer">
          One root a day, the whole family of words that grows from it, and a picture for each
          one. Written plainly, whichever country you are in. Writing and speaking are marked
          against the IELTS bands, so you can see band 7 coming.
        </p>
      </div>

      {/* ---- Where you are ---- */}
      <section className="border border-[#B3241B] bg-[#FFF3F2] p-4 sm:p-5" data-testid="english-progress">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["Level", p.level, "english-stat-level"],
            ["Words learned", p.words, "english-stat-words"],
            ["Day streak", p.streak, "english-stat-streak"],
            ["Overall band", p.overall_band ?? "—", "english-stat-band"]].map(([l, v, t]) => (
            <div key={t} className="border border-border bg-white p-3" data-testid={t}>
              <p className="font-display text-xl font-bold text-[#0F172A]">{v}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm text-slate-600" data-testid="english-access">
          {a.state === "trial" && `Free week — ${a.days_left} day${a.days_left === 1 ? "" : "s"} left.`}
          {a.state === "new" && `Your free week starts today.`}
          {a.state === "subscribed" && `Subscribed (${a.plan}) until ${a.until}.`}
          {a.state === "sponsored" && `${a.by} is paying for your seat.`}
          {a.state === "lapsed" && "Your free week is up — subscribe to carry on."}
          {" "}Aiming for {p.exam} band {p.target_band}.
          {p.hit_target && " You are there — well done."}
        </p>
        {(a.state === "trial" || a.state === "lapsed" || a.state === "new") && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="english-subscribe-monthly" size="sm" disabled={busy}
              onClick={() => subscribe("monthly")}>
              {data.plans.monthly.label}
            </Button>
            <Button data-testid="english-subscribe-yearly" size="sm" variant="outline" disabled={busy}
              onClick={() => subscribe("yearly")}>
              {data.plans.yearly.label}
            </Button>
          </div>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <select data-testid="english-exam" aria-label="Exam" value={aim.exam}
            onChange={(e) => setAim({ ...aim, exam: e.target.value })}
            className="tap-safe w-full border border-border bg-white px-3 py-2 text-sm">
            {data.exams.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <Input data-testid="english-target" type="number" min="4" max="9" step="0.5"
            value={aim.target_band} onChange={(e) => setAim({ ...aim, target_band: e.target.value })} />
          <Input data-testid="english-country" placeholder="Your country" value={aim.country}
            onChange={(e) => setAim({ ...aim, country: e.target.value })} />
          <Input data-testid="english-language" placeholder="First language" value={aim.first_language}
            onChange={(e) => setAim({ ...aim, first_language: e.target.value })} />
        </div>
        <Button data-testid="english-aim-save" size="sm" variant="outline" className="mt-2"
          disabled={busy}
          onClick={() => act(() => api.post("/english/aim", {
            exam: aim.exam, target_band: Number(aim.target_band) || 7,
            country: aim.country, first_language: aim.first_language }), "Saved")}>
          Save my aim
        </Button>
      </section>

      {/* ---- Tabs ---- */}
      <div className="flex flex-wrap gap-2" data-testid="english-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            data-testid={`english-tab-${key}`}
            className={`tap-safe inline-flex items-center gap-1.5 border px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
              tab === key ? "border-[#B3241B] bg-[#B3241B] text-white" : "border-border bg-white text-slate-600 hover:bg-slate-50"
            }`}>
            <Icon size={16} weight="duotone" /> {label}
          </button>
        ))}
      </div>

      {tab === "lesson" && (
        <section data-testid="english-lesson-panel">
          <Button data-testid="english-new-lesson" disabled={busy} onClick={newLesson}>
            {lesson ? "Next root" : "Start today's lesson"}
          </Button>
          {lesson && (
            <div className="mt-4 space-y-4">
              <div className="border border-border bg-white p-4">
                <p className="font-display text-xl font-bold text-[#0F172A]">
                  <span className="italic">{lesson.root}</span>
                  <span className="ml-2 text-sm font-normal text-slate-500">{lesson.origin}</span>
                </p>
                <p className="mt-2 text-base leading-relaxed text-slate-700">{lesson.story}</p>
                <ul className="mt-3 space-y-2">
                  {lesson.words.map((w, i) => (
                    <li key={w.word} className="border-l-2 border-[#B3241B] pl-3"
                      data-testid={`english-word-${i}`}>
                      <p className="font-semibold text-[#0F172A]">
                        {w.word} {w.say_it && <span className="text-xs font-normal text-slate-400">{w.say_it}</span>}
                      </p>
                      <p className="text-sm text-slate-600">{w.meaning}</p>
                      {w.picture && <p className="text-sm italic text-slate-500">Picture it: {w.picture}</p>}
                      {w.sentence && <p className="text-sm text-slate-600">“{w.sentence}”</p>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-border bg-white p-4" data-testid="english-passage">
                <p className="text-base leading-relaxed text-slate-700">{lesson.passage}</p>
              </div>
              <div className="border border-border bg-white p-4">
                <p className="overline text-slate-500">Self-test</p>
                {lesson.questions.map((q, i) => (
                  <div key={i} className="mt-3" data-testid={`english-question-${i}`}>
                    <p className="text-sm font-semibold text-[#0F172A]">{i + 1}. {q.q}</p>
                    <div className="mt-1 grid gap-1">
                      {q.options.map((o, oi) => {
                        const mark = marks?.marks?.[i];
                        const correct = mark && mark.answer === oi;
                        const wrongPick = mark && mark.yours === oi && !mark.right;
                        return (
                          <button key={oi} type="button" disabled={!!marks}
                            onClick={() => setPicked({ ...picked, [i]: oi })}
                            data-testid={`english-option-${i}-${oi}`}
                            className={`tap-safe border px-3 py-2 text-left text-sm ${
                              correct ? "border-[#0B5C2E] bg-[#F3FAF5] font-semibold"
                                : wrongPick ? "border-[#B3241B] bg-[#FFECEB] line-through"
                                : picked[i] === oi && !marks ? "border-[#B3241B] bg-[#FFF3F2]"
                                : "border-border bg-white"
                            }`}>
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {!marks ? (
                  <Button data-testid="english-mark-lesson" className="mt-3" disabled={busy}
                    onClick={markLesson}>Mark my answers</Button>
                ) : (
                  <p className={`mt-3 font-semibold ${
                    marks.score >= Math.ceil(marks.out_of / 2) ? "text-[#0B5C2E]" : "text-[#B3241B]"
                  }`} data-testid="english-lesson-score">
                    {marks.score} out of {marks.out_of}. {lesson.tip}
                  </p>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "writing" && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="english-writing-panel">
          <p className="text-sm font-semibold text-[#0F172A]">{writing.task}</p>
          <textarea data-testid="english-writing-text" rows={10} value={writing.text}
            onChange={(e) => setWriting({ ...writing, text: e.target.value })}
            placeholder="Write your answer here — at least 20 words, 250 for a real task 2"
            className="mt-3 w-full border border-border p-3 text-base" />
          <Button data-testid="english-writing-submit" className="mt-3" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data: out } = await api.post("/english/writing", {
                  task: writing.task, text: writing.text, exam: aim.exam });
                setWritten(out); load();
              } catch (e) { oops(e); } finally { setBusy(false); }
            }}>
            Mark my writing
          </Button>
          {written && (
            <div className="mt-4 border-t border-border pt-4" data-testid="english-writing-result">
              <p className="font-display text-2xl font-bold text-[#0B5C2E]">
                Band {written.band}{written.toefl ? ` · TOEFL ${written.toefl}` : ""}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {Object.entries(written.criteria || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}
              </p>
              {written.strengths?.length > 0 && (
                <p className="mt-2 text-sm text-slate-600">Good: {written.strengths.join("; ")}</p>
              )}
              <ul className="mt-2 space-y-1">
                {(written.fixes || []).map((f, i) => (
                  <li key={i} className="text-sm text-slate-600" data-testid={`english-fix-${i}`}>
                    <span className="line-through">{f.was}</span> → <span className="font-semibold">{f.better}</span>
                    {f.why ? ` — ${f.why}` : ""}
                  </li>
                ))}
              </ul>
              {written.model_answer && (
                <p className="mt-3 border-l-2 border-[#B3241B] pl-3 text-base leading-relaxed text-slate-700">
                  {written.model_answer}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {tab === "speaking" && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="english-speaking-panel">
          <p className="text-sm font-semibold text-[#0F172A]">
            Describe a place in your country that you would take a visitor to, and say why.
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Speak for a minute or two. We transcribe what you actually said and mark it.
          </p>
          <Button data-testid="english-record" className="mt-3" disabled={busy && !recording}
            onClick={record} variant={recording ? "outline" : "default"}>
            {recording ? <><Stop size={16} className="mr-1.5" /> Stop and mark it</>
              : <><Microphone size={16} className="mr-1.5" /> Start recording</>}
          </Button>
          {spoken && (
            <div className="mt-4 border-t border-border pt-4" data-testid="english-speaking-result">
              <p className="font-display text-2xl font-bold text-[#0B5C2E]">
                Band {spoken.band}{spoken.toefl ? ` · TOEFL ${spoken.toefl}` : ""}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {Object.entries(spoken.criteria || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}
                {spoken.words_per_minute ? ` · ${spoken.words_per_minute} words a minute` : ""}
              </p>
              <p className="mt-2 text-sm italic text-slate-600">“{spoken.transcript}”</p>
              {spoken.mispronounced?.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {spoken.mispronounced.map((m, i) => (
                    <li key={i} className="text-sm text-slate-600" data-testid={`english-sound-${i}`}>
                      {m.word} — say it {m.say}
                    </li>
                  ))}
                </ul>
              )}
              {spoken.next && <p className="mt-2 text-sm font-semibold text-[#B3241B]">{spoken.next}</p>}
            </div>
          )}
        </section>
      )}

      {tab === "listening" && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="english-listening-panel">
          <Button data-testid="english-new-listening" disabled={busy} onClick={newListening}>
            <Headphones size={16} className="mr-1.5" /> Play a new passage
          </Button>
          {heard && (
            <div className="mt-4">
              <p className="font-semibold text-[#0F172A]">{heard.title}</p>
              <p className="mt-1 text-sm text-slate-500">
                Listen — the text stays hidden. Tap play again to hear it once more.
              </p>
              <Button data-testid="english-replay" size="sm" variant="outline" className="mt-2"
                onClick={() => {
                  const s = new SpeechSynthesisUtterance(heard.passage);
                  s.lang = "en-GB"; s.rate = heard.speed === "slow" ? 0.85 : 1;
                  window.speechSynthesis.cancel(); window.speechSynthesis.speak(s);
                }}>
                Play again
              </Button>
              {heard.questions.map((q, i) => (
                <div key={i} className="mt-3" data-testid={`english-hq-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{i + 1}. {q.q}</p>
                  <div className="mt-1 grid gap-1">
                    {q.options.map((o, oi) => (
                      <button key={oi} type="button" onClick={() => setHAnswers({ ...hAnswers, [i]: oi })}
                        data-testid={`english-hoption-${i}-${oi}`}
                        className={`tap-safe border px-3 py-2 text-left text-sm ${
                          hAnswers[i] === oi ? "border-[#B3241B] bg-[#FFF3F2]" : "border-border bg-white"
                        }`}>
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {!hMarks ? (
                <Button data-testid="english-mark-listening" className="mt-3" disabled={busy}
                  onClick={async () => {
                    const answers = heard.questions.map((_, i) => (hAnswers[i] ?? -1));
                    if (answers.some((x) => x < 0)) return toast.error("Answer every question first");
                    const r = await act(() => api.post(`/english/listening/${heard.id}/answers`,
                      { answers }), "Marked");
                    if (r) setHMarks(r.data);
                  }}>
                  Mark my answers
                </Button>
              ) : (
                <p className="mt-3 font-semibold text-[#0B5C2E]" data-testid="english-listening-score">
                  {hMarks.score} out of {hMarks.out_of} · band {hMarks.band}. {heard.tip}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {tab === "tutor" && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="english-tutor-panel">
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {chat.length === 0 && (
              <p className="text-sm text-slate-500">
                Say anything — about your day, your job, your exam. Your tutor keeps the thread
                and corrects one thing each time.
              </p>
            )}
            {chat.map((m, i) => (
              <p key={i} data-testid={`english-chat-${i}`}
                className={`border px-3 py-2 text-sm ${
                  m.who === "learner" ? "ml-8 border-border bg-slate-50 text-slate-700"
                    : "mr-8 border-[#B3241B] bg-[#FFF3F2] text-[#0F172A]"
                }`}>
                {m.text}
              </p>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input data-testid="english-chat-input" value={say} placeholder="Say something…"
              onChange={(e) => setSay(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendChat()} />
            <Button data-testid="english-chat-send" disabled={busy} onClick={sendChat}>Send</Button>
          </div>
        </section>
      )}

      {/* ---- Certificates ---- */}
      <section data-testid="english-certificates">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
          <Certificate size={18} weight="duotone" className="text-[#B3241B]" /> Certificates
        </h2>
        {data.certificates.length === 0 ? (
          <p className="mt-2 text-sm text-slate-600" data-testid="english-certificates-empty">
            Learn 40 words and your {data.levels[0]} certificate is ready.
            {p.words > 0 ? ` You have ${p.words}.` : ""}
          </p>
        ) : (
          <ul className="mt-2 space-y-1">
            {data.certificates.map((c, i) => (
              <li key={c.id} className="text-sm text-slate-600" data-testid={`english-certificate-${i}`}>
                {c.level} · {c.words} words · {c.reference} · {c.awarded_on}
              </li>
            ))}
          </ul>
        )}
        {p.words >= 40 && (
          <Button data-testid="english-claim-certificate" size="sm" variant="outline" className="mt-2"
            disabled={busy} onClick={() => act(() => api.get("/english/certificate"), "Certificate issued")}>
            Claim my {p.level} certificate
          </Button>
        )}
      </section>

      {/* ---- Employer seats ---- */}
      {data.can_manage && (
        <section className="border border-border bg-white p-4 sm:p-5" data-testid="english-seats-panel">
          <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">
            Put your staff on the course
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Their seat is on you, and they learn free. One email address per line.
          </p>
          <textarea data-testid="english-seats-input" rows={4} value={seats}
            onChange={(e) => setSeats(e.target.value)}
            className="mt-3 w-full border border-border p-3 text-base" />
          <Button data-testid="english-seats-submit" className="mt-2" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data: out } = await api.post("/english/sponsor", {
                  emails: seats.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
                });
                setSeats("");
                toast.success(`${out.seats} seat${out.seats === 1 ? "" : "s"} given`);
                if (out.unknown?.length) {
                  toast.error(`Not seated: ${out.unknown.join("; ")}`);
                }
                load();
              } catch (e) { oops(e); } finally { setBusy(false); }
            }}>
            Give the seats
          </Button>
          {data.seats.length > 0 && (
            <ul className="mt-3 space-y-0.5">
              {data.seats.map((s, i) => (
                <li key={s.id} className="text-sm text-slate-600" data-testid={`english-seat-${i}`}>
                  {s.name} · {s.level} · {(s.words || []).length} words · {s.streak} day streak
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
